import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, BoxBorder } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React, { useEffect } from 'react';

import { OperationType } from '../../../../../../../../../components/OperationType';
import { TableContentItemText } from '../../../../../../../../../components/TableContent';
import { useEquityAssetController } from '../../../../../hooks/products/Equity';
import { AssetTitleWithImage } from '../../../components/AssetTitleWithImage';
import { AssetValueWithPercentage } from '../../../components/AssetValueWithPercentage';
import { LateralBar } from '../../../components/LateralBar';
import {
  EQUITY_ORDER_TYPE_LABEL,
  EQUITY_ORDER_TYPE_OPTIONS,
  EquityListItemTypes,
} from '../ListItem';

import { EquityItemSelectedTypes } from '.';

export const EquityItemSelectedTablet: React.FC<EquityItemSelectedTypes.Props> = ({
  offer,
  onUpdate,
  isBatchTicket = true,
}) => {
  const id = offer.productIdNormalized;
  const {
    amountItem,
    priceItem,
    totalPrice,
    handleChangeAmountItem,
    handleChangePriceItem,
    totalPriceIsInvalid,
    orderType,
    handleChangeOrderType,
  } = useEquityAssetController(offer);

  useEffect(() => {
    if (offer.orderValue === priceItem || !priceItem) {
      return;
    }
    const updatedOffer = { ...offer, orderValue: priceItem || 0 };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceItem]);

  useEffect(() => {
    if (offer.orderQuantity === amountItem || !amountItem) {
      return;
    }
    const updatedOffer = { ...offer, orderQuantity: amountItem || 0 };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountItem]);

  useEffect(() => {
    if (isBatchTicket) {
      return;
    }
    const offerOrderType = offer.orderType ?? 'Limit';
    if (offerOrderType === orderType) {
      return;
    }
    const nextOrderValue =
      orderType === 'Market' ? Number((offer.quote.lastPrice ?? 0).toFixed(2)) : priceItem;
    onUpdate({
      ...offer,
      orderType,
      orderQuantity: amountItem,
      orderValue: nextOrderValue,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType, isBatchTicket]);

  return (
    <UI.List testId="selected-product" height="container">
      <LateralBar
        backgroundColor={Color['product.rendavariavelbuy']}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <VStack space="stack.xxxs" flex={1}>
        <HStack flex={1} justifyContent="space-between">
          <Stack w="1/4">
            <AssetTitleWithImage
              image={offer.imageNormalized || ''}
              title={offer.productNameNormalized || ''}
              subtitle={offer.symbolNormalized || ''}
            />
          </Stack>
          <UI.ListItem
            testId="selected-product-last-price"
            title="Último preço"
            stackProps={{ w: '1/4' }}>
            <AssetValueWithPercentage
              value={offer.lastPriceNormalized}
              percentage={offer.priceChangeTaxValueNormalized}
              iconColor={offer.priceChangeTaxColorNormalized}
              updateDate={offer.tradeDateNormalized}
            />
          </UI.ListItem>
          <UI.ListItem
            testId="selected-product-operation"
            title="Operação"
            stackProps={{ w: '1/4' }}>
            <OperationType title="Compra" />
          </UI.ListItem>
          <UI.ListItem
            testId="selected-product-validity"
            title="Validade da ordem"
            stackProps={{ w: '1/4' }}>
            <TableContentItemText>Hoje</TableContentItemText>
          </UI.ListItem>
        </HStack>
        <HStack flex={1} justifyContent="space-between">
          <UI.ListItem
            testId="selected-product-type"
            title="Tipo da ordem"
            stackProps={{ w: '1/4' }}>
            {isBatchTicket ? (
              <TableContentItemText>Limitada</TableContentItemText>
            ) : (
              <Stack
                mr={Spacing['inline.xxxs']}
                borderRadius={BoxBorder.thick}
                bgColor="backgroundshade"
                w="full">
                <UI.Select
                  testID={`equity-order-type-select-conference-${id}`}
                  options={EQUITY_ORDER_TYPE_OPTIONS}
                  selectedValue={orderType}
                  onValueChange={(value) => {
                    handleChangeOrderType(value as EquityListItemTypes.OrderType);
                  }}
                  placeholder={EQUITY_ORDER_TYPE_LABEL.Limit}
                />
              </Stack>
            )}
          </UI.ListItem>
          <UI.ListItem testId="selected-product-name" title="Quantidade" stackProps={{ w: '1/4' }}>
            <Stack pr="inline.nano" w="full">
              <UI.InputStepper
                stepperValue={offer.quote.lotSize || 1}
                max={1000000}
                min={offer.quote.lotSize || 1}
                name={`equity-amount-order-input${id}`}
                keyboardType="numeric"
                defaultValue={amountItem}
                onChange={handleChangeAmountItem}
                showErrorMessage={false}
                {...(totalPriceIsInvalid && {
                  customError: 'Quantidade inválida',
                })}
              />
            </Stack>
          </UI.ListItem>
          <UI.ListItem
            testId="selected-product-name"
            title="Preço por ativo"
            stackProps={{ w: '1/4' }}>
            <Stack pr="inline.nano" w="full">
              <UI.InputStepper
                stepperValue={0.01}
                max={1000000}
                min={0.01}
                keyboardType="numeric"
                ignoreStepperDivision
                maskOption="money"
                name={`equity-amount-price${id}`}
                defaultValue={priceItem || 0.01}
                onChange={handleChangePriceItem}
                showErrorMessage={false}
                disabled={!isBatchTicket && orderType === 'Market'}
                {...(totalPriceIsInvalid && {
                  customError: 'Valor inválido',
                })}
              />
            </Stack>
          </UI.ListItem>
          <UI.ListItem
            testId="selected-product-name"
            title="Total estimado"
            stackProps={{ w: '1/4' }}>
            <TableContentItemText>{Utils.Mask.Money(totalPrice)}</TableContentItemText>
          </UI.ListItem>
        </HStack>
      </VStack>
    </UI.List>
  );
};
